/**
 * Project View, Volt Entrypoint.
 *
 * Self-contained module consolidating the component and resource
 * declarations that the late-binding pattern currently spreads across
 * route-definitions/project-view.ts and views/project/src/route.ts.
 *
 * Loaded lazily by string reference at navigation time.
 */

import { Skeleton } from '@townsquare/griffin-main-header/skeleton';
import { onboardingResource } from '@townsquare/onboarding/resource';
import type { Entrypoint } from '@townsquare/router/volt/types';

import { jiraIntegrationLinkResource } from './resources/JiraIntegrationLink';
import {
	projectDecisionsTabResource,
	projectLearningsTabResource,
	projectRisksTabResource,
} from './resources/ProjectLearningsTab';
import { projectPageAggResource } from './resources/ProjectPageAgg';
import { projectUpdatesResource, projectViewResource } from './resources/ProjectView';
import { projectViewAsideResource } from './resources/ProjectViewAside';
import { projectViewHeaderResource } from './resources/ProjectViewHeader';

const ProjectPageEntrypoint: Entrypoint = {
	// TODO: loader becomes a JSResourceReference when Relay entrypoints land.
	root: {
		loader: () => import(/* webpackChunkName: "project-view" */ './components/ProjectView'),
	},

	aside: {
		loader: () =>
			import(/* webpackChunkName: "project-view-aside" */ './components/ProjectViewAside'),
	},

	header: {
		loader: () =>
			import(/* webpackChunkName: "project-view-header" */ './components/ProjectHeaderRefreshed'),
		// TODO: loading moves to a skeleton registry (string ref) so the
		// entrypoint has zero synchronous imports of view code.
		loading: Skeleton,
	},

	// aggContainer wraps the layout to batch sidebar and header GraphQL
	// queries into a single request via a separate Relay environment.
	// This is a manual workaround for what Relay fragment composition
	// provides natively. Removing it would let the entrypoint own all
	// data loading through resources and getPreloadProps alone.
	aggContainer: {
		loader: () => import(/* webpackChunkName: "project-page-agg" */ './components/ProjectPageAgg'),
	},

	// TODO: resources move to string refs in getPreloadProps.extraProps.resources,
	// resolved by a per-app resource registry. This removes the synchronous
	// resource imports and makes the entrypoint a pure declaration.
	resources: [
		projectLearningsTabResource,
		projectRisksTabResource,
		projectDecisionsTabResource,
		projectViewResource,
		projectViewAsideResource,
		projectViewHeaderResource,
		projectUpdatesResource,
		jiraIntegrationLinkResource,
		onboardingResource,
		projectPageAggResource,
	],
};

export default ProjectPageEntrypoint;
